import { ref, onMounted, ComputedRef, Ref, watch, computed } from '@vue/composition-api';
import type { NAVIGATION_LIST } from './config';
import UIConfigModel from '@/models/ui-config';
import { useApolloModel } from 'vue-apollo-model';

const tvSdkVisitedTag = 'tvSdkVisitedUrl';
const tvBtnShowTag = 'tvSdkShowUrl';

export function useTvVersion(curTvSdkUrl: ComputedRef<string>, navigationList: Ref<typeof NAVIGATION_LIST>, log: (logInfo: unknown) => void) {
    const visitedTvSdkUrl = ref('');
    const sendShowLogUrl = ref('');
    const showNewTvVersionIcon = ref(false);
    onMounted(() => {
        visitedTvSdkUrl.value = localStorage.getItem(tvSdkVisitedTag) ?? '';
        sendShowLogUrl.value = localStorage.getItem(tvBtnShowTag) ?? '';
        watch(() => curTvSdkUrl.value, curUrl => {
            if (curUrl && visitedTvSdkUrl.value !== curTvSdkUrl.value) {
                showNewTvVersionIcon.value = true;
            }
            // 如果有TV新版本且没有上报过曝光，则上报一次曝光埋点
            if (showNewTvVersionIcon.value && sendShowLogUrl.value !== curTvSdkUrl.value) {
                log({
                    type: 'SHOW',
                    event_name: 'UNIFY_REDPOINT',
                    event_value: {
                        icon: 'TV端',
                    },
                    urlPage: {
                        page: 'APP_GENERAL',
                    },
                });
                localStorage.setItem(tvBtnShowTag, curTvSdkUrl.value);
            }
        });
    });
    watch(() => showNewTvVersionIcon.value, showNewTv => {
        const tvItem = navigationList.value.find(item => item.id === 'tv');
        tvItem?.subIcons?.forEach(sub => {
            if (sub.id === 'new-tv-version') {
                sub.show = showNewTv;
            }
        });
    });
    function clickNewTv(newTvUrl: string) {
        visitedTvSdkUrl.value = localStorage.getItem(tvSdkVisitedTag) ?? '';
        // 使用上一次访问过的sdk版本和最新配置版本信息比较，如果不一致且有新版本icon情况下，上报一次埋点
        if (visitedTvSdkUrl.value !== newTvUrl && showNewTvVersionIcon.value) {
            log({
                type: 'CLICK',
                event_name: 'UNIFY_REDPOINT',
                event_value: {
                    icon: 'TV端',
                },
                urlPage: {
                    page: 'APP_GENERAL',
                },
            });
        }
        // 更新最新版本 url 到本地
        localStorage.setItem(tvSdkVisitedTag, newTvUrl);
        showNewTvVersionIcon.value = false;
    }
    return {
        visitedTvSdkUrl,
        clickNewTv,
    };
}

export function useHeaderUIConfig(isHeaderDark: ComputedRef<boolean>) {
    const uiConfigModel = useApolloModel(UIConfigModel);
    const headerUIdata = computed(() => uiConfigModel.topBarUIData);
    const easyNavUIData = computed(() => headerUIdata.value.leftItemConfig);
    const subListUIData = computed(() => headerUIdata.value.subList);
    const searchUIData = computed(() => headerUIdata.value.searchItemConfig);
    const navToolBarUIData = computed(() => headerUIdata.value.rightItemConfig);
    const headerStyle = computed(() => {
        if (headerUIdata.value) {
            if (isHeaderDark.value) {
                return {
                    background: headerUIdata.value.background,
                };
            }
            return {
                background: headerUIdata.value.backgroundTop,
            };
        }
        return {};
    });
    const logoStyle = computed(() => {
        if (headerUIdata.value && headerUIdata.value.logoImgUrl) {
            return {
                backgroundImage: `url(${headerUIdata.value.logoImgUrl})`,
            };
        }
    });
    const loginStyle = computed(() => {
        if (headerUIdata.value && headerUIdata.value.login) {
            return {
                background: headerUIdata.value.login.loginButtonBackground,
                color: headerUIdata.value.login.loginButtonTextColor,
            };
        }
    });
    return {
        headerStyle,
        logoStyle,
        easyNavUIData,
        searchUIData,
        navToolBarUIData,
        loginStyle,
        subListUIData,
    };
}